// Shared resolution/days preference — legacy `StatisticsParametersService`,
// persisted the same way (localStorage `statisticsResolution` /
// `statisticsDays`) and used by Dashboard, GlobalProfitSummaryPage,
// CoinTreasuryPage, CoinSheetPage (days only for positions/closed-price
// interval) and TransactionsPage (days only).
import { useCallback, useState } from 'react'
import type { Resolution } from '../../api/statisticsClient'

export interface StatisticsPreset {
  resolution: Resolution
  days: number
  label: string
}

/** Exactly the 4 legacy presets (`v3-statistics-parameters.component.ts`),
 * in their on-screen order (owner 2026-10-13: no divider between the daily and
 * hourly groups in the dropdown). */
export const STATISTICS_PRESETS: StatisticsPreset[] = [
  { resolution: 'daily', days: 90, label: 'Last 90 days - daily' },
  { resolution: 'daily', days: 30, label: 'Last 30 days - daily' },
  { resolution: 'hourly', days: 30, label: 'Last 30 days - hourly' },
  { resolution: 'hourly', days: 7, label: 'Last 7 days - hourly' },
]

const DEFAULT_PRESET = STATISTICS_PRESETS[0]!

function presetKey(resolution: Resolution, days: number): string {
  return `${resolution}:${days}`
}

function readStored(): StatisticsPreset {
  try {
    const resolution = (localStorage.getItem('statisticsResolution') as Resolution | null) ?? DEFAULT_PRESET.resolution
    const daysRaw = localStorage.getItem('statisticsDays')
    const days = daysRaw ? Number(daysRaw) : DEFAULT_PRESET.days
    const found = STATISTICS_PRESETS.find((p) => p.resolution === resolution && p.days === days)
    return found ?? DEFAULT_PRESET
  } catch {
    return DEFAULT_PRESET
  }
}

/** Legacy `setResolution`/`setDays` wrote independently, but every caller UI
 * only ever offers the 4 combined presets above — kept as one persisted pair. */
export function useStatisticsParams() {
  const [preset, setPresetState] = useState<StatisticsPreset>(readStored)

  const setPreset = useCallback((next: StatisticsPreset) => {
    setPresetState(next)
    try {
      localStorage.setItem('statisticsResolution', next.resolution)
      localStorage.setItem('statisticsDays', String(next.days))
    } catch {
      // localStorage unavailable (private mode) — in-memory selection still works.
    }
  }, [])

  return { resolution: preset.resolution, days: preset.days, preset, setPreset }
}

export { presetKey }
